import isApplicationUneditable from '@bybeam/platform-lib/application/isApplicationUneditable';
import { ApplicationWithCase } from '@bybeam/platform-lib/application/reapplication';
import assertExhaustive from '@bybeam/platform-lib/utilities/assertExhaustive';
import {
  Application,
  ApplicationConfig,
  ApplicationStatus,
  CaseStatus,
  Nullable,
  Partner,
  Program,
  StatusOverride,
  User,
} from '@bybeam/platform-types';
import { ButtonProps } from '@platform-ui-common/components/Button/index.js';
import replacePlaceholders, {
  PlaceholderSource,
} from '@platform-ui-common/utilities/placeholder.js';
import { StatusBlockProps } from '@platform-ui/application/components/StatusBlock/index.js';
import ApplicantRoutes from '@platform-ui/application/utils/ApplicantRoutes.js';
import resumeApplication from '@platform-ui/application/utils/resumeApplication.js';
import { POSTHOG_FEATURE_FLAGS } from '@platform-ui/providers/posthog.js';
import { makeRoute } from '@platform-ui/utilities/Routes.js';
import { applicationHasPrefilledAnswers } from '@platform-ui/utilities/application/config.js';
import { DEFAULT_STATUSES } from '@platform-ui/utilities/application/overrides.js';
import { usePostHog } from 'posthog-js/react';
import { ApplicationStatusBlock } from '../types.js';
import mapPaymentsStatus from './mapPaymentsStatus.js';

export type ApplicationStatusProps = {
  application?: Application;
  config: ApplicationConfig;
  partner: Partner;
  program: Program;
  placeholders: PlaceholderSource;
  user: User;
};

export function mapToApplicationStatus(application?: Application): ApplicationStatus {
  if (!application) return 'Application';

  switch (application.case.status) {
    case CaseStatus.Withdrawn:
      return isApplicationUneditable(application as ApplicationWithCase)
        ? 'WithdrawnClosed'
        : 'WithdrawnOpen';
    case CaseStatus.InProgress:
      return 'InProgress';
    case CaseStatus.Incomplete:
      // TODO: remove this condition and always return Incomplete post-backfill of InProgress status
      return application.submittedAt ? 'Incomplete' : 'InProgress';
    case CaseStatus.ReadyForReview:
      return 'AwaitingReview';
    case CaseStatus.InReview:
    case CaseStatus.PendingCertification:
      return 'InReview';
    case CaseStatus.FiscalReview:
    case CaseStatus.Approved:
    case CaseStatus.PaymentSent:
      return 'Approved';
    case CaseStatus.Denied:
      return 'Denied';
    case CaseStatus.Archived:
      return 'Unknown';
    default:
      return assertExhaustive(application.case.status);
  }
}

function getStatusBlockButton(
  { application, config, program }: ApplicationStatusProps,
  posthog,
): Nullable<Pick<ButtonProps, 'link' | 'children'>> {
  const status = mapToApplicationStatus(application);
  const { statuses: overrideStatuses } = config.overrides ?? {};
  const { action } = { ...overrideStatuses?.find((override) => override.status === status) };

  switch (status) {
    case 'Application':
      return {
        link: makeRoute(ApplicantRoutes.PROGRAM_INTRO, { programId: program.id }),
        children: action || 'Start new application',
      };
    case 'Incomplete':
      if (posthog.isFeatureEnabled(POSTHOG_FEATURE_FLAGS.resubmissionRequests)) {
        return {
          link: makeRoute(makeRoute(ApplicantRoutes.CASE_DETAILS, { caseId: application.case.id })),
          children: action || 'Review your application',
        };
      }
      return {
        link: makeRoute(resumeApplication(config, application), {
          applicationId: application.id,
        }),
        children: action || 'Continue your application',
      };
    case 'InProgress':
    case 'WithdrawnOpen':
      return {
        link: makeRoute(resumeApplication(config, application), {
          applicationId: application.id,
        }),
        children: action || 'Continue your application',
      };
  }
}

function getBlockCopy({
  application,
  config,
  placeholders,
  program,
  user,
}: ApplicationStatusProps): Pick<StatusBlockProps, 'label' | 'message'> {
  const status = mapToApplicationStatus(application);
  const { statuses } = config.overrides ?? {};
  const {
    label: rawLabel,
    message: rawMessage,
    referralMessage: rawReferralMessage,
  } = {
    ...DEFAULT_STATUSES[status],
    ...statuses?.find((override) => override.status === status),
  } as Pick<StatusOverride, 'label' | 'message' | 'referralMessage'>;

  const label = replacePlaceholders(rawLabel, placeholders);
  const message = replacePlaceholders(rawMessage, placeholders);
  const referralMessage = replacePlaceholders(
    rawReferralMessage ||
      'You have been referred to this program by {PARTNER_NAME}’s staff based on your previous application information. A referral does not guarantee you will qualify.',
    placeholders,
  );

  if (status !== 'Application') return { label, message };

  const hasReferral = user.referrals?.find((referral) => referral.program.id === program.id);
  return {
    label,
    message: [hasReferral && referralMessage, message].filter(Boolean).join('\n\n'),
  };
}

function getBlockChips({
  application,
  user,
  program,
  config,
  partner,
}: ApplicationStatusProps): StatusBlockProps['chips'] {
  const hasReferral = user.referrals?.find((referral) => referral.program.id === program.id);
  const isPrefilled = applicationHasPrefilledAnswers(
    config,
    user?.applicantProfile,
    partner?.applicantProfileConfigs,
  );
  const status = mapToApplicationStatus(application);
  const response = [];
  if (hasReferral) response.push('referral');
  if (['Application', 'Incomplete', 'InProgress', 'WithdrawnOpen'].includes(status) && isPrefilled)
    response.push('prefilled');
  return response;
}

export default function getStatusBlock(
  props: ApplicationStatusProps,
  posthog: ReturnType<typeof usePostHog>,
): Omit<ApplicationStatusBlock, 'title' | 'key'> {
  const chips = getBlockChips(props);
  const status = mapToApplicationStatus(props.application);
  const paymentBlock = mapPaymentsStatus(props);
  if (paymentBlock) return { ...paymentBlock, status, chips };

  return { ...getBlockCopy(props), button: getStatusBlockButton(props, posthog), status, chips };
}
